'use client';
import { useRef } from 'react';

import { cn } from '@/lib/utils';

import { BadgeSBProps } from '@/components/atoms/badge/BadgeSB';
import { ImageSBProps } from '@/components/atoms/image/ImageSB';
import Header from '@/components/atoms/layout/header';
import HeaderMainSB from '@/components/atoms/layout/header/HeaderMainSB';
import MobileHeaderMainSB from '@/components/atoms/layout/header/MobileHeaderMainSB';
import { LinkButtonSBProps } from '@/components/atoms/linkButton/LinkSB';
import { Page } from '@/components/cms/types/entry';

import { PricePerProps } from '@/features/shared/PricePer';

// TODO add to headerprops
export type HeaderItemLevel<OtherProps extends Record<string, unknown>> = {
  id: string;
  level: string;
  title: React.ReactNode;
  badge?: BadgeSBProps;
  link?: LinkButtonSBProps;
  customLink?: React.ReactNode;
} & OtherProps;

export type MaybeHeaderItemLevel<T extends Record<string, unknown>> =
  | HeaderItemLevel<T>
  | undefined;

export interface HeaderProps {
  header: {
    id: string;
    firstLevels: HeaderItemLevel<{
      extraInfo?: {
        title: string;
        list?: LinkButtonSBProps[];
        link?: LinkButtonSBProps;
        banner?: {
          icon: ImageSBProps;
          title: string;
          description: string;
          button: LinkButtonSBProps;
        };
      }[];
      secondLevels?: HeaderItemLevel<{
        thirdLevels?: HeaderItemLevel<{
          description?: string;
          price?: PricePerProps;
        }>[];
      }>[];
    }>[];
  };
}

export interface HeaderSBProps {
  data: Page & {
    logo: ImageSBProps;
    logoLink?: string;
    logoAlignment?: 'left' | 'center';
    extraSectionLinks?: LinkButtonSBProps[];
    showLanguages?: boolean;
  };
}

export default function HeaderSB({ data }: HeaderSBProps) {
  const headerRef = useRef<HTMLDivElement>(null);

  return (
    <Header ref={headerRef}>
      <div className={cn('block w-full xl:hidden')}>
        <MobileHeaderMainSB
          block={{
            firstLevels: (data.header?.firstLevels ??
              []) as HeaderProps['header']['firstLevels'],
            logo: data.logo,
            logoLink: data.logoLink,
            logoAlignment: data.logoAlignment || 'left',
            bottomSection: data.extraSectionLinks,
            showLanguages: !!data.showLanguages,
          }}
        />
      </div>
      <div className={cn('mx-auto my-0 hidden w-full max-w-[1440px] xl:block')}>
        <HeaderMainSB
          block={{
            firstLevels: (data.header?.firstLevels ??
              []) as HeaderProps['header']['firstLevels'],
            logo: data.logo,
            logoLink: data.logoLink,
            logoAlignment: data.logoAlignment || 'left',
            rightSection: data.extraSectionLinks,
            showLanguages: !!data.showLanguages,
          }}
          headerRef={headerRef}
        />
      </div>
    </Header>
  );
}
